import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, CheckCircle, Circle, Plus, RefreshCw, Phone, Mail, Calendar, User, Trash2, UserCheck, Filter, Clipboard, ExternalLink, Search, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';

export default function ChecklistPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [adminNoteDialogOpen, setAdminNoteDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewAllTasks, setViewAllTasks] = useState(user?.role === 'clinic_admin');
  const [isClinicTask, setIsClinicTask] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [pendingUpdateItem, setPendingUpdateItem] = useState(null);
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [patientSearchValue, setPatientSearchValue] = useState("");
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Form data for manual item creation
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_phone: '',
    task_title: '',
    patient_phone: '',
    action_type: 'call',
    due_datetime: '',
    last_visit_date: '',
    prescription_notes: '',
    assigned_to: '',
    assigned_to_name: ''
  });

  // Assignment form
  const [assignToUserId, setAssignToUserId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [multiUserMode, setMultiUserMode] = useState(false);

  useEffect(() => {
    loadChecklist();
    loadUsers();
    loadPatients();
  }, []);

  useEffect(() => {
    if (checklist) {
      loadChecklist();
    }
  }, [viewAllTasks]);

  // Scroll to item if hash present
  useEffect(() => {
    if (checklist && window.location.hash) {
      const itemId = window.location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(itemId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
          }, 2000);
        }
      }, 300);
    }
  }, [checklist]);

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/checklist/today?view_all=${viewAllTasks}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setChecklist(data);
      } else {
        console.error('Failed to load checklist');
        toast.error('Failed to load checklist');
      }
    } catch (error) {
      console.error('Error loading checklist:', error);
      toast.error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/users/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter nurses and clinic admins
        const filtered = data.filter(u => ['nurse', 'clinic_admin'].includes(u.role));
        setUsers(filtered);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPatients = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/patients/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPatients(data);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const handleGenerateFromFollowups = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/checklist/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        await loadChecklist();
      } else {
        toast.error('Failed to generate checklist items');
      }
    } catch (error) {
      console.error('Error generating checklist:', error);
      toast.error('Failed to generate checklist items');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleComplete = async (item) => {
    // Handle comma-separated multi-user assignments
    const assignedUserIds = item.assigned_to ? item.assigned_to.split(',').map(id => id.trim()) : [];
    const isOwner = user?.id && assignedUserIds.includes(user.id);
    const isAdmin = user?.role === 'clinic_admin' || user?.role === 'superadmin';
    const newStatus = item.status === 'completed' ? 'pending' : 'completed';
    
    // Debug logging
    console.log('Completion Debug:', {
      userId: user?.id,
      userRole: user?.role,
      itemAssignedTo: item.assigned_to,
      parsedIds: assignedUserIds,
      isOwner,
      isAdmin
    });
    
    // Check permissions - Allow if user is owner OR if user is admin
    if (!isOwner && !isAdmin) {
      toast.error('You can only update your own tasks');
      console.error('Permission denied:', { isOwner, isAdmin });
      return;
    }
    
    // If admin updating other's task (not their own), require note
    if (isAdmin && !isOwner && newStatus === 'completed') {
      setPendingUpdateItem({ itemId: item.item_id, newStatus });
      setAdminNoteDialogOpen(true);
      return;
    }
    
    // Normal update (own task or admin updating own task - no note required)
    await performUpdate(item.item_id, newStatus, null);
  };

  const performUpdate = async (itemId, newStatus, note) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      let url = `${API_URL}/api/checklist/items/${itemId}?status=${newStatus}`;
      if (note) {
        url += `&note=${encodeURIComponent(note)}`;
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Item marked as ${newStatus}`);
        await loadChecklist();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Failed to update item');
    }
  };

  const handleAdminUpdate = async () => {
    if (!adminNote.trim()) {
      toast.error('Please provide a reason for updating this task');
      return;
    }
    
    await performUpdate(pendingUpdateItem.itemId, pendingUpdateItem.newStatus, adminNote);
    setAdminNoteDialogOpen(false);
    setAdminNote("");
    setPendingUpdateItem(null);
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/checklist/items/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Item deleted');
        await loadChecklist();
      } else {
        toast.error('Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patient_id: patientId,
        patient_name: patient.full_name,
        patient_phone: patient.phone || '',
        last_visit_date: patient.last_visit_date || ''
      });
    }
  };

  const handleCreateManualItem = async (e) => {
    e.preventDefault();
    
    // Validation: For clinic tasks need task_title, for patient tasks need patient_id
    if (isClinicTask) {
      if (!formData.task_title || !formData.action_type || !formData.due_datetime) {
        toast.error('Please fill in required fields (Task Title, Action Type, Due Date)');
        return;
      }
    } else {
      if (!formData.patient_id || !formData.action_type || !formData.due_datetime) {
        toast.error('Please fill in required fields (Patient, Action Type, Due Date)');
        return;
      }
    }

    try {
      const token = localStorage.getItem('auth_token');
      
      // Prepare payload with multi-user support
      let payload = {...formData};
      if (multiUserMode && selectedUserIds.length > 0) {
        // Multi-user assignment
        const assignedNames = selectedUserIds.map(id => {
          const u = users.find(user => user.id === id);
          const name = u?.full_name || u?.email || '';
          return id === user?.id ? `Self (${name})` : name;
        });
        payload.assigned_to = selectedUserIds[0]; // First user as primary
        payload.assigned_to_name = assignedNames.join(', ');
        payload.assigned_users = selectedUserIds;
      }
      
      const response = await fetch(`${API_URL}/api/checklist/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)  // Send payload, not formData
      });

      if (response.ok) {
        toast.success('Checklist item created');
        setCreateDialogOpen(false);
        setIsClinicTask(false);
        setMultiUserMode(false);
        setSelectedUserIds([]);
        setFormData({
          patient_id: '',
          patient_name: '',
          patient_phone: '',
          task_title: '',
          action_type: 'call',
          due_datetime: '',
          last_visit_date: '',
          prescription_notes: '',
          assigned_to: '',
          assigned_to_name: ''
        });
        await loadChecklist();
      } else {
        toast.error('Failed to create item');
      }
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error('Failed to create item');
    }
  };

  const handleBulkAssign = async () => {
    // Check if at least one user is selected
    if (multiUserMode) {
      if (selectedUserIds.length === 0) {
        toast.error('Please select at least one user');
        return;
      }
    } else {
      if (!assignToUserId) {
        toast.error('Please select a user to assign to');
        return;
      }
    }

    if (selectedItems.length === 0) {
      toast.error('Please select items to assign');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      
      // Prepare payload based on mode
      const payload = multiUserMode
        ? {
            item_ids: selectedItems,
            assigned_users: selectedUserIds,
            assigned_to: selectedUserIds[0] // Fallback for backward compatibility
          }
        : {
            item_ids: selectedItems,
            assigned_to: assignToUserId
          };
      
      const response = await fetch(`${API_URL}/api/checklist/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setAssignDialogOpen(false);
        setSelectedItems([]);
        setAssignToUserId('');
        setSelectedUserIds([]);
        setMultiUserMode(false);
        await loadChecklist();
      } else {
        toast.error('Failed to assign items');
      }
    } catch (error) {
      console.error('Error assigning items:', error);
      toast.error('Failed to assign items');
    }
  };

  const handleToggleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const handleSelectAll = (items) => {
    const itemIds = items.map(i => i.item_id);
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(itemIds);
    }
  };

  if (!checklist || loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8 flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#652D90" }} />
        </div>
      </Layout>
    );
  }

  const pendingItems = checklist.items?.filter(i => i.status === 'pending') || [];
  const completedItems = checklist.items?.filter(i => i.status === 'completed') || [];

  // Render a single task card (used in both grouped and flat views)
  const renderTaskCard = (item, canDelete) => {
    const assignedUserIds = item.assigned_to ? item.assigned_to.split(',').map(id => id.trim()) : [];
    const isCreator = item.created_by === user?.id;
    const canDeleteTask = isCreator; // Can only delete if you created it
    
    return (
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-3 flex-1">
        {/* Checkbox - Only for Admin */}
        {(user?.role === 'clinic_admin' || user?.role === 'superadmin') && (
          <input
            type="checkbox"
            checked={selectedItems.includes(item.item_id)}
            onChange={() => handleToggleSelectItem(item.item_id)}
            className="mt-1 h-4 w-4 cursor-pointer"
          />
        )}
        
        <div className="flex-1">
          {/* Task Title at Top */}
          {item.task_title && (
            <h3 className="font-semibold text-gray-900 text-lg mb-1">{item.task_title}</h3>
          )}
          
          {/* Clinic Task Badge or Patient Info */}
          {item.patient_id ? (
            <button
              onClick={() => navigate(`/patients?id=${item.patient_id}`)}
              className="font-medium text-gray-700 hover:text-purple-600 flex items-center gap-1 group mb-2"
            >
              Patient: {item.patient_name}
              <span className="text-xs text-gray-500">({item.patient_id})</span>
              <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : item.is_clinic_task ? (
            <span className="inline-block px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-700 mb-2">
              📋 Clinic Task
            </span>
          ) : (
            <p className="text-sm text-gray-600 mb-2">{item.patient_name || 'Task'}</p>
          )}
          
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
            {item.action_type === 'call' ? (
              <span className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                Call: {item.patient_phone}
              </span>
            ) : item.action_type === 'email' ? (
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Email
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Clipboard className="h-3 w-3" />
                Task
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(item.due_datetime).toLocaleString()}
            </span>
          </div>
          {item.prescription_notes && (
            <div className="flex items-start gap-1 mt-2 text-sm text-gray-500">
              <Clipboard className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>{item.prescription_notes}</p>
            </div>
          )}
          
          {/* Show assignment info only in non-grouped view */}
          {!viewAllTasks && item.assigned_to && user?.role !== 'nurse' && (
            <div className="flex items-center gap-1 mt-2 text-sm">
              <User className="h-3 w-3 text-purple-600" />
              {assignedUserIds.includes(user?.id) ? (
                <span className="text-purple-600">
                  Assigned to: Self
                </span>
              ) : (
                <span className="text-purple-600">Assigned to: {item.assigned_to_name || 'Unknown'}</span>
              )}
            </div>
          )}
          
          {/* Show creator info if different from assignee */}
          {viewAllTasks && item.created_by && item.created_by !== item.assigned_to && (
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
              <span>Created by: {item.created_by === user?.id ? 'You' : item.created_by_name}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 ml-4">
        <Button
          onClick={() => handleToggleComplete(item)}
          size="sm"
          style={{ backgroundColor: "#10b981" }}
          className="text-white"
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Mark Completed
        </Button>
        {canDeleteTask && (
          <Button
            onClick={() => handleDeleteItem(item.item_id)}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>Today's Checklist</h1>
            <p className="text-gray-600 mt-1">
              {checklist.date} • {pendingItems.length} pending, {completedItems.length} completed
              {!viewAllTasks && user?.role !== 'clinic_admin' && <span className="ml-2 text-purple-600">(My Tasks Only)</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'clinic_admin' && (
              <Button
                onClick={() => setViewAllTasks(!viewAllTasks)}
                variant="outline"
              >
                <Filter className="mr-2 h-4 w-4" />
                {viewAllTasks ? 'My Tasks Only' : 'View All Tasks'}
              </Button>
            )}
            <Button
              onClick={handleGenerateFromFollowups}
              disabled={generating}
              variant="outline"
            >
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Generate from Follow-ups
            </Button>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              style={{ backgroundColor: "#652D90" }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Manual Item
            </Button>
          </div>
        </div>

        {/* Empty State - No Items at All */}
        {pendingItems.length === 0 && completedItems.length === 0 && (
          <Card className="rounded-2xl border-[#e5e5e5]">
            <CardContent className="py-16">
              <div className="text-center">
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Checklist Items Yet</h3>
                <p className="text-gray-500 mb-6">
                  Get started by generating items from pending follow-ups or create manual tasks
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={handleGenerateFromFollowups}
                    disabled={generating}
                    style={{ backgroundColor: "#652D90" }}
                  >
                    {generating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Generate from Follow-ups
                  </Button>
                  <Button
                    onClick={() => setCreateDialogOpen(true)}
                    variant="outline"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Manual Item
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bulk Actions - Only for Admin */}
        {selectedItems.length > 0 && (user?.role === 'clinic_admin' || user?.role === 'superadmin') && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {selectedItems.length} item(s) selected
                </span>
                <Button
                  onClick={() => setAssignDialogOpen(true)}
                  variant="outline"
                  size="sm"
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  Assign Selected
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Items - Only show if there are ANY items */}
        {(pendingItems.length > 0 || completedItems.length > 0) && (
          <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
                <Circle className="h-5 w-5" />
                Pending Items ({pendingItems.length})
              </CardTitle>
              {/* Select All Checkbox - Only for Admin */}
              {pendingItems.length > 0 && (user?.role === 'clinic_admin' || user?.role === 'superadmin') && (
                <input
                  type="checkbox"
                  checked={selectedItems.length === pendingItems.length}
                  onChange={() => handleSelectAll(pendingItems)}
                  className="h-4 w-4 cursor-pointer"
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                <p>All tasks completed! Great job! 🎉</p>
              </div>
            ) : viewAllTasks && (user?.role === 'clinic_admin' || user?.role === 'superadmin') ? (
              // Admin View All Mode - Group by Assignee
              <div className="space-y-6">
                {(() => {
                  // Group tasks by assignee
                  const grouped = {};
                  pendingItems.forEach(item => {
                    // Clean up the assignee name display
                    let displayName = item.assigned_to_name || 'Unassigned';
                    
                    // If assigned_to_name contains "Self", extract the actual name
                    if (displayName.includes('Self (')) {
                      // Extract name from "Self (Name - role)" format
                      const match = displayName.match(/Self \(([^-]+)/);
                      if (match) {
                        const actualName = match[1].trim();
                        // Check if this is the current user
                        const assignedUserIds = item.assigned_to ? item.assigned_to.split(',').map(id => id.trim()) : [];
                        if (assignedUserIds.includes(user?.id)) {
                          displayName = `Self (${user?.full_name || user?.email})`;
                        } else {
                          displayName = actualName;
                        }
                      }
                    }
                    
                    if (!grouped[displayName]) grouped[displayName] = [];
                    grouped[displayName].push(item);
                  });
                  
                  return Object.entries(grouped).map(([assigneeName, items]) => (
                    <div key={assigneeName} className="space-y-3">
                      {/* Group Header */}
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <User className="h-4 w-4 text-purple-600" />
                        <h3 className="font-semibold text-purple-700">{assigneeName}</h3>
                        <span className="text-sm text-gray-500">({items.length} task{items.length !== 1 ? 's' : ''})</span>
                      </div>
                      
                      {/* Tasks in this group */}
                      {items.map((item) => (
                        <div
                          key={item.item_id}
                          id={item.item_id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-all ml-6"
                        >
                          {renderTaskCard(item)}
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            ) : (
              // Normal View or Nurse View - Flat List
              <div className="space-y-3">
                {pendingItems.map((item) => (
                  <div
                    key={item.item_id}
                    id={item.item_id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-all"
                  >
                    {renderTaskCard(item)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Completed Items */}
        {completedItems.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Completed Items ({completedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {completedItems.map((item) => (
                  <div
                    key={item.item_id}
                    className="border rounded-lg p-4 bg-green-50 opacity-75"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 line-through">{item.patient_name}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          {item.action_type === 'call' ? (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {item.patient_phone}
                            </span>
                          ) : item.action_type === 'email' ? (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              Email
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Clipboard className="h-3 w-3" />
                              Task
                            </span>
                          )}
                          <span className="text-green-600">
                            ✓ Completed {item.completed_at && `at ${new Date(item.completed_at).toLocaleTimeString()}`}
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleToggleComplete(item)}
                        variant="outline"
                        size="sm"
                      >
                        Mark Pending
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Manual Item Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Manual Checklist Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateManualItem} className="space-y-4">
              {/* Clinic Task Checkbox */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="isClinicTask"
                  checked={isClinicTask}
                  onChange={(e) => {
                    setIsClinicTask(e.target.checked);
                    if (e.target.checked) {
                      setFormData({...formData, patient_id: '', patient_name: '', patient_phone: ''});
                    }
                  }}
                  className="h-4 w-4"
                />
                <label htmlFor="isClinicTask" className="text-sm font-medium cursor-pointer">
                  <Clipboard className="inline h-4 w-4 mr-1" />
                  This is a clinic task (not patient-related)
                </label>
              </div>

              {/* Patient or Task Title */}
              {isClinicTask ? (
                <div className="space-y-2">
                  <Label>Task Title *</Label>
                  <Input
                    value={formData.task_title}
                    onChange={(e) => setFormData({...formData, task_title: e.target.value})}
                    placeholder="e.g., Clean equipment, Order supplies, Staff meeting"
                    required
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Patient *</Label>
                  <Popover open={patientSearchOpen} onOpenChange={setPatientSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={patientSearchOpen}
                        className="w-full justify-between"
                      >
                        {formData.patient_id
                          ? patients.find((p) => p.id === formData.patient_id)?.full_name
                          : "Select patient..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <Command>
                        <CommandInput placeholder="Search patient..." />
                        <CommandList>
                          <CommandEmpty>No patient found.</CommandEmpty>
                          <CommandGroup>
                            {patients.map((patient) => (
                              <CommandItem
                                key={patient.id}
                                value={`${patient.full_name} ${patient.phone}`}
                                onSelect={() => {
                                  handlePatientSelect(patient.id);
                                  setPatientSearchOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.patient_id === patient.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {patient.full_name} - {patient.phone}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Action Type *</Label>
                  <Select
                    value={formData.action_type}
                    onValueChange={(val) => setFormData({...formData, action_type: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="task">Task/Todo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Due Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={formData.due_datetime}
                    onChange={(e) => setFormData({...formData, due_datetime: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={formData.prescription_notes}
                  onChange={(e) => setFormData({...formData, prescription_notes: e.target.value})}
                  placeholder="Optional notes"
                />
              </div>

              {/* Assignment - Only for Admins */}
              {user?.role !== 'nurse' && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                  <Label className="text-base font-semibold">Assignment (Optional)</Label>
                  
                  {/* Multi-user toggle */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="createMultiUser"
                      checked={multiUserMode}
                      onChange={(e) => {
                        setMultiUserMode(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedUserIds([]);
                        } else {
                          setFormData({...formData, assigned_to: '', assigned_to_name: ''});
                        }
                      }}
                      className="h-4 w-4"
                    />
                    <label htmlFor="createMultiUser" className="text-sm cursor-pointer">
                      Assign to multiple users
                    </label>
                  </div>

                  {multiUserMode ? (
                    /* Multi-User Selection */
                    <div className="space-y-2">
                      <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                        {/* Self option */}
                        <label className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user?.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds([...selectedUserIds, user?.id]);
                              } else {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== user?.id));
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <span className="text-sm font-semibold text-purple-600">
                            Self ({user?.full_name || user?.email})
                          </span>
                        </label>
                        {/* Other users */}
                        <div className="border-t pt-2">
                          {users.filter(u => u.id !== user?.id).map(u => (
                            <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedUserIds.includes(u.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUserIds([...selectedUserIds, u.id]);
                                  } else {
                                    setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              <span className="text-sm">{u.full_name || u.email} ({u.role})</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {selectedUserIds.length > 0 && (
                        <p className="text-xs text-gray-500">
                          Selected: {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Single User Selection */
                    <Select
                      value={formData.assigned_to}
                      onValueChange={(val) => {
                        const selectedUser = users.find(u => u.id === val);
                        setFormData({
                          ...formData,
                          assigned_to: val,
                          assigned_to_name: selectedUser ? selectedUser.full_name || selectedUser.email : ''
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select user (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.id}>
                          Self ({user?.full_name || user?.email})
                        </SelectItem>
                        {users.filter(u => u.id !== user?.id).map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name || u.email} ({u.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" style={{ backgroundColor: "#652D90" }}>
                  Create Item
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Assign Selected Items</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Assign {selectedItems.length} selected item(s) to:
              </p>
              
              {/* Mode Toggle */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="multiUserMode"
                  checked={multiUserMode}
                  onChange={(e) => {
                    setMultiUserMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedUserIds([]);
                    } else {
                      setAssignToUserId('');
                    }
                  }}
                  className="h-4 w-4"
                />
                <label htmlFor="multiUserMode" className="text-sm font-medium cursor-pointer">
                  Assign to multiple users
                </label>
              </div>

              {multiUserMode ? (
                /* Multi-User Selection */
                <div className="space-y-2">
                  <Label>Select Users (Check all that apply)</Label>
                  <div className="border rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                    {/* Self option at top */}
                    <label className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user?.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUserIds([...selectedUserIds, user?.id]);
                          } else {
                            setSelectedUserIds(selectedUserIds.filter(id => id !== user?.id));
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <span className="text-sm font-semibold text-purple-600">
                        Self ({user?.full_name || user?.email} - {user?.role})
                      </span>
                    </label>
                    <div className="border-t pt-2">
                      {users.filter(u => u.id !== user?.id).map(u => (
                        <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds([...selectedUserIds, u.id]);
                              } else {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                              }
                            }}
                            className="h-4 w-4"
                          />
                          <span className="text-sm">{u.full_name || u.email} ({u.role})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {selectedUserIds.length > 0 && (
                    <p className="text-xs text-gray-500">
                      Selected: {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ) : (
                /* Single User Selection */
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select value={assignToUserId} onValueChange={setAssignToUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={user?.id}>
                        Self ({user?.full_name || user?.email} - {user?.role})
                      </SelectItem>
                      {users.filter(u => u.id !== user?.id).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name || u.email} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setAssignDialogOpen(false);
                setMultiUserMode(false);
                setSelectedUserIds([]);
              }}>
                Cancel
              </Button>
              <Button onClick={handleBulkAssign} style={{ backgroundColor: "#652D90" }}>
                Assign
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Note Dialog */}
        <Dialog open={adminNoteDialogOpen} onOpenChange={setAdminNoteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Admin Note Required</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                You are updating a task assigned to another user. Please provide a reason:
              </p>
              <Textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="e.g., Helping cover for absent staff member, Emergency situation, etc."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setAdminNoteDialogOpen(false);
                setAdminNote("");
                setPendingUpdateItem(null);
              }}>
                Cancel
              </Button>
              <Button onClick={handleAdminUpdate} style={{ backgroundColor: "#652D90" }}>
                Update with Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
